import { Check, ChevronDown, X } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactElement } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Badge } from "./Badge";
import { cn } from "./cn";
import type { SelectOption } from "./Select";

/** Tallest the list may get, and the smallest it is worth showing. */
const MAX_PANEL_HEIGHT = 288;
const MIN_PANEL_HEIGHT = 144;

interface Anchor {
  left: number;
  width: number;
  top?: number;
  bottom?: number;
  maxHeight: number;
}

export interface MultiSelectProps {
  options: SelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Shows a filter box once the list is long enough to need one. */
  searchThreshold?: number;
  id?: string;
  /**
   * `sm` matches the grid's filter row — the same height, radius and type as
   * the plain `<select>` beside it. The default is the form control.
   */
  size?: "sm" | "md";
}

/**
 * Multi-choice picker — the `MultiSelectComboBox` equivalent.
 *
 * Selected values render as removable chips rather than a comma-joined string,
 * so a product with eight tags stays readable and any one of them can be
 * dropped without reopening the list.
 */
export function MultiSelect({
  options,
  value,
  onChange,
  size = "md",
  placeholder,
  disabled,
  searchThreshold = 8,
  id,
}: MultiSelectProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const container = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const [anchor, setAnchor] = useState<Anchor | null>(null);

  /**
   * The list is positioned against the viewport rather than the trigger, because
   * an absolutely-positioned child was being clipped by whatever scrolling box
   * it happened to sit in — most visibly inside a Dialog, whose body scrolls, so
   * a list of part groups showed as a sliver a couple of rows tall.
   */
  const place = useCallback(() => {
    const rect = trigger.current?.getBoundingClientRect();
    if (!rect) return;
    const gap = 4;
    const margin = 8;
    const below = window.innerHeight - rect.bottom - gap - margin;
    const above = rect.top - gap - margin;
    // Flip up only when that genuinely gives more room, so the list stays put
    // in the common case.
    const up = below < Math.min(MAX_PANEL_HEIGHT, above) && above > below;
    setAnchor({
      left: rect.left,
      width: rect.width,
      ...(up
        ? { bottom: window.innerHeight - rect.top + gap }
        : { top: rect.bottom + gap }),
      maxHeight: Math.max(MIN_PANEL_HEIGHT, Math.min(MAX_PANEL_HEIGHT, up ? above : below)),
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      // The list lives in a portal, so it is not inside `container`.
      if (!container.current?.contains(target) && !panel.current?.contains(target)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    // Capture, so scrolling any ancestor container repositions the list too.
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, place]);

  const selected = options.filter((option) => value.includes(option.value));
  const needle = query.trim().toLowerCase();
  const visible = needle
    ? options.filter((option) => option.label.toLowerCase().includes(needle))
    : options;

  const toggle = (optionValue: string) =>
    onChange(
      value.includes(optionValue)
        ? value.filter((entry) => entry !== optionValue)
        : [...value, optionValue],
    );

  return (
    <div ref={container} className="relative">
      <button
        ref={trigger}
        type="button"
        id={id}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className={cn(
          "flex w-full items-center gap-1.5 border border-line bg-surface text-left",
          size === "sm"
            ? "min-h-6 rounded px-1.5 py-0 text-[12px]"
            : "min-h-9 rounded-md px-2 py-1 text-sm max-md:min-h-11",
          "focus-visible:border-accent focus-visible:outline-2 focus-visible:outline-accent",
          "disabled:cursor-not-allowed disabled:opacity-60",
        )}
      >
        <span className={cn("flex min-w-0 flex-1 flex-wrap", size === "sm" ? "gap-0.5" : "gap-1")}>
          {selected.length === 0 ? (
            <span className={cn("text-ink-faint", size === "sm" ? "px-0.5" : "px-1")}>{placeholder ?? ""}</span>
          ) : size === "sm" && selected.length > 1 ? (
            // Chips do not fit a six-pixel-shorter row twice over; past one
            // selection the trigger just says how many.
            <span className="px-0.5 text-ink">{`${selected.length} ✓`}</span>
          ) : (
            selected.map((option) => (
              <Badge key={option.value} variant="accent" size="sm">
                {option.label}
                {!disabled && (
                  <X
                    className="ml-1 size-3 cursor-pointer"
                    onClick={(event) => {
                      // Removing a chip must not also open the list.
                      event.stopPropagation();
                      toggle(option.value);
                    }}
                  />
                )}
              </Badge>
            ))
          )}
        </span>
        <ChevronDown className={cn("shrink-0 text-ink-faint", size === "sm" ? "size-3" : "size-4")} />
      </button>

      {open && !disabled && anchor && renderPanel(
        <div
          ref={panel}
          style={{
            position: "fixed",
            left: anchor.left,
            // At least the trigger's width, wider when an option needs it: a
            // filter-row trigger is narrow, and "READY TO SHIP" cut to "READY
            // TO" is a different status.
            minWidth: anchor.width,
            width: "max-content",
            maxWidth: Math.max(anchor.width, 320),
            ...(anchor.top !== undefined ? { top: anchor.top } : { bottom: anchor.bottom }),
            maxHeight: anchor.maxHeight,
          }}
          className="z-50 overflow-auto rounded-md border border-line bg-surface-over p-1 shadow-overlay"
        >
          {options.length >= searchThreshold && (
            <input
              value={query}
              autoFocus
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t("grid.search", "Search")}
              className="mb-1 h-8 w-full rounded border border-line bg-surface px-2 text-[13px] text-ink"
            />
          )}

          {visible.length === 0 ? (
            <p className="px-2 py-3 text-center text-[12px] text-ink-faint">{t("select.no-matches", "No matches")}</p>
          ) : (
            visible.map((option) => {
              const on = value.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={on}
                  onClick={() => toggle(option.value)}
                  className={cn(
                    "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[13px]",
                    on ? "bg-accent-soft text-accent" : "text-ink hover:bg-surface-sunk",
                  )}
                >
                  <Check className={cn("size-3.5 shrink-0", !on && "invisible")} />
                  <span className="truncate">{option.label}</span>
                </button>
              );
            })
          )}
        </div>,
        trigger.current,
      )}
    </div>
  );
}

/**
 * Puts the list in the nearest `<dialog>` when there is one, otherwise in the
 * body. It has to be the dialog itself: `showModal()` promotes it to the top
 * layer, so a list portalled to `document.body` would render behind it.
 */
function renderPanel(node: ReactElement, trigger: HTMLElement | null) {
  const host = trigger?.closest("dialog") ?? document.body;
  return createPortal(node, host);
}
